import type { SupportedLocale } from "../i18n-locales";
import { localizedPath } from "./routes";
import { SITE_URL } from "./seo";

export const SECRETARIAT_SLUGS: Record<SupportedLocale, string> = {
  it: "segreteria-virtuale",
  en: "virtual-assistant",
  es: "secretaria-virtual",
  fr: "secretariat-virtuel",
  de: "virtuelle-assistenz",
};
export const SECRETARIAT_RATES = {
  assistant: 30,
  calendar: 25,
  documents: 35,
} as const;
export type HourlyService = keyof typeof SECRETARIAT_RATES;
export type SecretariatPlan = HourlyService | "monthly";
export const MONTHLY_PRICE = 300;
export const MONTHLY_HOURS = 8;
// Editorial translations are drafts until reviewed by a human.
export const SECRETARIAT_TRANSLATION_STATUS = {
  it: "source",
  en: "needs_review",
  es: "needs_review",
  fr: "needs_review",
  de: "needs_review",
} as const;

type ServiceCopy = { title: string; description: string; fit: string };
type Copy = {
  title: string;
  meta: string;
  eyebrow: string;
  headline: string;
  intro: string;
  personTitle: string;
  personText: string;
  hourlyTitle: string;
  hourlyIntro: string;
  fitLabel: string;
  hourlyUnit: string;
  monthUnit: string;
  vat: string;
  choose: string;
  monthlyTitle: string;
  monthlyIntro: string;
  monthlyLimit: string;
  monthlyDifference: string;
  monthlyPoints: string[];
  processTitle: string;
  process: { title: string; description: string }[];
  requestTitle: string;
  requestIntro: string;
  serviceLabel: string;
  hoursLabel: string;
  hoursHint: string;
  hoursError: string;
  totalLabel: string;
  requestCta: string;
  discover: string;
  back: string;
  summaryLabel: string;
  faqTitle: string;
  faqs: { question: string; answer: string }[];
  services: Record<Exclude<SecretariatPlan, "monthly">, ServiceCopy>;
};

export const SECRETARIAT_COPY: Record<SupportedLocale, Copy> = {
  it: {
    title: "Segreteria virtuale con Valentina",
    meta: "Valentina, per Enkey Agency: segreteria da remoto, agenda e documenti. Scegli le ore o l’assistenza avanzata a 300 €/mese, fino a 8 ore. IVA esclusa.",
    eyebrow: "Enkey Agency · Segreteria da remoto",
    headline: "Più spazio al tuo lavoro. Più ordine nelle tue giornate.",
    intro:
      "Le email da gestire, gli appuntamenti da incastrare, quel preventivo ancora da preparare: il lavoro organizzativo occupa tempo anche quando non compare in agenda. Valentina ti affianca da remoto con i servizi di segreteria di Enkey Agency, per dare alle attività quotidiane l’attenzione che richiedono.",
    personTitle: "Il tuo riferimento è Valentina",
    personText:
      "Un supporto dedicato a professionisti e aziende, costruito sulle attività da svolgere. Con Valentina definisci cosa delegare, quali informazioni condividere e quali passaggi mantenere sotto la tua approvazione. Da un’esigenza precisa a un affiancamento mensile, il punto di partenza è il tuo modo di lavorare.",
    hourlyTitle: "Scegli il supporto, decidi le ore",
    hourlyIntro:
      "Tre servizi con obiettivi diversi. Seleziona quello più adatto e indica le ore: il totale si aggiorna subito, prima di inviare la richiesta.",
    fitLabel: "Quando sceglierlo",
    hourlyUnit: "/ora",
    monthUnit: "/mese",
    vat: "IVA esclusa",
    choose: "Scegli questo servizio",
    monthlyTitle: "Assistenza aziendale avanzata",
    monthlyIntro:
      "Un affiancamento mensile flessibile per chi deve coordinare più attività, oltre al singolo compito. Valentina parte dalle tue esigenze e concorda con te un piano di supporto che può combinare organizzazione, comunicazioni e back office.",
    monthlyLimit: "Fino a 8 ore complessive al mese",
    monthlyDifference:
      "La formula mensile è pensata per la continuità e il coordinamento: le ore vengono distribuite tra le attività concordate, entro il limite complessivo di 8 ore. Per un’attività circoscritta puoi invece scegliere uno dei servizi a tariffa oraria.",
    monthlyPoints: [
      "Analisi delle esigenze e definizione delle priorità",
      "Pianificazione condivisa delle attività del mese",
      "Supporto su più ambiti, secondo il piano concordato",
    ],
    processTitle: "Dalla richiesta al lavoro quotidiano",
    process: [
      {
        title: "Raccontaci cosa ti serve",
        description:
          "Scegli il servizio e le ore, oppure la formula mensile. Aggiungi le attività da gestire e le tue priorità.",
      },
      {
        title: "Definiamo insieme l’incarico",
        description:
          "Concordiamo attività, tempi e materiali necessari. Per il mensile definiamo come distribuire il supporto entro le 8 ore complessive.",
      },
      {
        title: "Organizziamo il lavoro",
        description:
          "Stabiliamo strumenti, informazioni da condividere e passaggi di approvazione, così Valentina può lavorare su indicazioni chiare.",
      },
    ],
    requestTitle: "Di quale supporto hai bisogno?",
    requestIntro:
      "Configura la richiesta e raccontaci le tue esigenze. L’invio serve a concordare l’incarico: non effettua un pagamento e non conferma automaticamente disponibilità o appuntamenti.",
    serviceLabel: "Servizio",
    hoursLabel: "Numero di ore",
    hoursHint: "Inserisci un numero intero di ore, a partire da 1.",
    hoursError: "Inserisci un numero intero di ore valido, almeno 1.",
    totalLabel: "Totale richiesto",
    requestCta: "Richiedi il supporto di Valentina",
    discover: "Scopri il servizio",
    back: "Tutti i servizi",
    summaryLabel: "Riepilogo della richiesta",
    faqTitle: "Prima di delegare",
    faqs: [
      {
        question: "Qual è la differenza tra segreteria, agenda e documenti?",
        answer:
          "La segreteria virtuale segue attività amministrative e organizzative di back office. Il servizio agenda si concentra su appuntamenti, calendario e promemoria. Il servizio documentale riguarda la preparazione di preventivi, offerte e modulistica, sulla base delle informazioni che fornisci.",
      },
      {
        question: "Che cosa comprende il mensile da 300 €?",
        answer:
          "È un servizio avanzato e flessibile, con analisi delle esigenze e pianificazione di più attività concordate. Comprende fino a 8 ore complessive al mese: il limite riguarda l’intero pacchetto, non ciascuna attività. Il prezzo è IVA esclusa.",
      },
      {
        question: "Posso scegliere quante ore richiedere?",
        answer:
          "Sì, per i tre servizi a tariffa oraria puoi indicare il numero di ore e vedere il totale IVA esclusa. Attività, tempi e disponibilità vengono concordati prima dell’avvio.",
      },
      {
        question: "Il servizio si svolge da remoto?",
        answer:
          "Sì. Il supporto è svolto da Valentina nell’ambito dei servizi Enkey Agency. Gli strumenti da utilizzare e le informazioni necessarie vengono concordati in base all’incarico.",
      },
    ],
    services: {
      assistant: {
        title: "Segreteria virtuale da remoto",
        description:
          "Supporto amministrativo e organizzativo per dare continuità alle attività quotidiane: email, appuntamenti, documenti, pratiche e back office. Un aiuto trasversale quando ci sono più piccoli compiti da tenere insieme.",
        fit: "Hai bisogno di delegare attività operative diverse, all’interno di un incarico definito.",
      },
      calendar: {
        title: "Gestione agenda e appuntamenti",
        description:
          "Organizzazione degli appuntamenti, pianificazione delle attività e invio dei promemoria. Il lavoro si concentra sul calendario professionale, per mantenere ordinati impegni, disponibilità e scadenze.",
        fit: "La tua priorità è organizzare il tempo e gli appuntamenti, senza un supporto amministrativo più ampio.",
      },
      documents: {
        title: "Creazione preventivi e documentazione",
        description:
          "Supporto nella preparazione di preventivi, offerte, documenti commerciali e modulistica aziendale. Valentina organizza le informazioni che fornisci e prepara i documenti secondo le tue indicazioni, per la tua verifica.",
        fit: "Devi trasformare dati e indicazioni commerciali in documenti ordinati e pronti da controllare.",
      },
    },
  },
  en: {
    title: "Virtual assistance with Valentina",
    meta: "Remote assistance from Valentina at Enkey Agency: administration, scheduling and documents. Choose hourly support or €300/month for up to 8 hours, excluding VAT.",
    eyebrow: "Enkey Agency · Remote assistance",
    headline: "More room for your work. More order in your day.",
    intro:
      "Emails to handle, appointments to arrange, a quote still waiting to be prepared: organisational work takes time even when it is not on your calendar. Valentina supports you remotely through Enkey Agency, giving everyday tasks the attention they need.",
    personTitle: "Valentina is your point of contact",
    personText:
      "Support for professionals and businesses, shaped around the tasks at hand. With Valentina, you define what to delegate, what information to share and which steps need your approval. Whether you need help with one task or monthly support, your way of working comes first.",
    hourlyTitle: "Choose your support and your hours",
    hourlyIntro:
      "Three services with different purposes. Select the best fit and enter your hours to see the total before sending your request.",
    fitLabel: "Choose this when",
    hourlyUnit: "/hour",
    monthUnit: "/month",
    vat: "Excluding VAT",
    choose: "Choose this service",
    monthlyTitle: "Advanced business assistance",
    monthlyIntro:
      "Flexible monthly support for coordinating several activities beyond a single task. Valentina reviews your needs with you and agrees a support plan combining organisation, communications and back-office work.",
    monthlyLimit: "Up to 8 hours in total per month",
    monthlyDifference:
      "The monthly plan supports continuity and coordination. Hours are distributed across agreed activities within the overall limit of 8 hours. For a specific task, choose an hourly service instead.",
    monthlyPoints: [
      "Review of your needs and priorities",
      "Joint planning of the month’s activities",
      "Support across several areas within the agreed plan",
    ],
    processTitle: "From your request to everyday work",
    process: [
      {
        title: "Tell us what you need",
        description:
          "Choose a service and hours, or the monthly plan. Describe your tasks and priorities.",
      },
      {
        title: "Agree the assignment",
        description:
          "We agree activities, timing and required materials. For the monthly plan, we decide how to allocate support within 8 hours in total.",
      },
      {
        title: "Organise the work",
        description:
          "We agree tools, information to share and approval steps so Valentina can work with clear instructions.",
      },
    ],
    requestTitle: "What support do you need?",
    requestIntro:
      "Configure your request and tell us about your needs. Sending it starts a discussion about the assignment; it does not take payment or automatically confirm availability or appointments.",
    serviceLabel: "Service",
    hoursLabel: "Number of hours",
    hoursHint: "Enter a whole number of hours, starting from 1.",
    hoursError: "Enter a valid whole number of hours, at least 1.",
    totalLabel: "Requested total",
    requestCta: "Request Valentina’s support",
    discover: "Explore the service",
    back: "All services",
    summaryLabel: "Request summary",
    faqTitle: "Before you delegate",
    faqs: [
      {
        question: "How do administration, scheduling and documents differ?",
        answer:
          "Virtual assistance covers administrative and organisational back-office tasks. Scheduling focuses on appointments, calendars and reminders. Document support covers quotes, offers and forms based on information you provide.",
      },
      {
        question: "What does the €300 monthly plan include?",
        answer:
          "Advanced, flexible support with a needs review and planning across agreed activities. It includes up to 8 hours in total per month, for the entire plan rather than each activity. The price excludes VAT.",
      },
      {
        question: "Can I choose the hours?",
        answer:
          "Yes. For the three hourly services, enter the number of hours to see the total excluding VAT. Activities, timing and availability are agreed before work starts.",
      },
      {
        question: "Is the service remote?",
        answer:
          "Yes. Valentina provides support as part of Enkey Agency’s services. The tools and information needed are agreed for each assignment.",
      },
    ],
    services: {
      assistant: {
        title: "Remote virtual assistance",
        description:
          "Administrative and organisational support for everyday work: emails, appointments, documents, paperwork and back office. Broad support when several small tasks need to be managed together.",
        fit: "You need to delegate different operational tasks within a defined assignment.",
      },
      calendar: {
        title: "Calendar and appointment management",
        description:
          "Appointment organisation, activity planning and reminders. This service focuses on your professional calendar, keeping commitments, availability and deadlines organised.",
        fit: "Your priority is scheduling your time and appointments without broader administrative support.",
      },
      documents: {
        title: "Quotes and business documents",
        description:
          "Help preparing quotes, offers, commercial documents and business forms. Valentina organises the information you provide and prepares documents following your instructions, ready for your review.",
        fit: "You need to turn commercial information into organised documents to check.",
      },
    },
  },
  es: {
    title: "Secretaría virtual con Valentina",
    meta: "Valentina, de Enkey Agency: apoyo remoto, agenda y documentos. Elige las horas o la asistencia avanzada por 300 €/mes, hasta 8 horas. IVA no incluido.",
    eyebrow: "Enkey Agency · Secretaría remota",
    headline: "Más espacio para tu trabajo. Más orden en tu día.",
    intro:
      "Correos pendientes, citas que organizar, un presupuesto por preparar: el trabajo organizativo requiere tiempo aunque no figure en la agenda. Valentina te acompaña a distancia con los servicios de secretaría de Enkey Agency para atender las tareas del día a día.",
    personTitle: "Tu persona de referencia es Valentina",
    personText:
      "Apoyo para profesionales y empresas, adaptado a las tareas que necesitan realizar. Con Valentina defines qué delegar, qué información compartir y qué pasos requieren tu aprobación. Desde una necesidad concreta hasta un acompañamiento mensual, partimos de tu forma de trabajar.",
    hourlyTitle: "Elige el apoyo y las horas",
    hourlyIntro:
      "Tres servicios con objetivos distintos. Selecciona el adecuado e indica las horas para ver el total antes de enviar la solicitud.",
    fitLabel: "Cuándo elegirlo",
    hourlyUnit: "/hora",
    monthUnit: "/mes",
    vat: "IVA no incluido",
    choose: "Elegir este servicio",
    monthlyTitle: "Asistencia empresarial avanzada",
    monthlyIntro:
      "Apoyo mensual flexible para coordinar varias actividades, más allá de una tarea puntual. Valentina analiza tus necesidades y acuerda contigo un plan que puede combinar organización, comunicaciones y tareas administrativas.",
    monthlyLimit: "Hasta 8 horas en total al mes",
    monthlyDifference:
      "La modalidad mensual facilita la continuidad y la coordinación. Las horas se distribuyen entre las actividades acordadas, dentro del límite total de 8 horas. Para una tarea concreta puedes elegir un servicio por horas.",
    monthlyPoints: [
      "Análisis de necesidades y prioridades",
      "Planificación conjunta de las actividades del mes",
      "Apoyo en varias áreas según el plan acordado",
    ],
    processTitle: "De la solicitud al trabajo diario",
    process: [
      {
        title: "Cuéntanos qué necesitas",
        description:
          "Elige un servicio y las horas, o el plan mensual. Describe las tareas y tus prioridades.",
      },
      {
        title: "Definimos el encargo",
        description:
          "Acordamos actividades, plazos y materiales. En el plan mensual distribuimos el apoyo dentro de las 8 horas totales.",
      },
      {
        title: "Organizamos el trabajo",
        description:
          "Definimos herramientas, información que compartir y pasos de aprobación para que Valentina trabaje con indicaciones claras.",
      },
    ],
    requestTitle: "¿Qué apoyo necesitas?",
    requestIntro:
      "Configura tu solicitud y describe tus necesidades. El envío sirve para acordar el encargo: no realiza un pago ni confirma automáticamente disponibilidad o citas.",
    serviceLabel: "Servicio",
    hoursLabel: "Número de horas",
    hoursHint: "Introduce un número entero de horas, a partir de 1.",
    hoursError: "Introduce un número entero de horas válido, al menos 1.",
    totalLabel: "Total solicitado",
    requestCta: "Solicitar el apoyo de Valentina",
    discover: "Descubrir el servicio",
    back: "Todos los servicios",
    summaryLabel: "Resumen de la solicitud",
    faqTitle: "Antes de delegar",
    faqs: [
      {
        question: "¿En qué se diferencian secretaría, agenda y documentos?",
        answer:
          "La secretaría virtual cubre tareas administrativas y organizativas. El servicio de agenda se centra en citas, calendario y recordatorios. El documental comprende presupuestos, ofertas y formularios a partir de la información que facilitas.",
      },
      {
        question: "¿Qué incluye el plan mensual de 300 €?",
        answer:
          "Un servicio avanzado y flexible con análisis de necesidades y planificación de actividades acordadas. Incluye hasta 8 horas totales al mes para todo el paquete, no para cada actividad. IVA no incluido.",
      },
      {
        question: "¿Puedo elegir las horas?",
        answer:
          "Sí. En los tres servicios por horas puedes indicar la cantidad y ver el total sin IVA. Las actividades, los plazos y la disponibilidad se acuerdan antes de comenzar.",
      },
      {
        question: "¿El servicio es remoto?",
        answer:
          "Sí. Valentina presta el apoyo como parte de los servicios de Enkey Agency. Las herramientas y la información necesaria se acuerdan según el encargo.",
      },
    ],
    services: {
      assistant: {
        title: "Secretaría virtual remota",
        description:
          "Apoyo administrativo y organizativo para el día a día: correos, citas, documentos, trámites y tareas de oficina. Ayuda transversal cuando hay varias tareas pequeñas que coordinar.",
        fit: "Necesitas delegar distintas tareas operativas dentro de un encargo definido.",
      },
      calendar: {
        title: "Gestión de agenda y citas",
        description:
          "Organización de citas, planificación de actividades y envío de recordatorios. Se centra en el calendario profesional para ordenar compromisos, disponibilidad y plazos.",
        fit: "Tu prioridad es organizar el tiempo y las citas sin un apoyo administrativo más amplio.",
      },
      documents: {
        title: "Preparación de presupuestos y documentos",
        description:
          "Apoyo para preparar presupuestos, ofertas, documentos comerciales y formularios. Valentina organiza la información que facilitas y prepara los documentos según tus indicaciones para que los revises.",
        fit: "Necesitas convertir datos comerciales en documentos ordenados para revisar.",
      },
    },
  },
  fr: {
    title: "Secrétariat virtuel avec Valentina",
    meta: "Valentina, chez Enkey Agency : assistance à distance, agenda et documents. À l’heure ou en formule avancée à 300 €/mois, jusqu’à 8 heures. Prix HT.",
    eyebrow: "Enkey Agency · Secrétariat à distance",
    headline: "Plus de place pour votre travail. Plus d’ordre au quotidien.",
    intro:
      "Des e-mails à traiter, des rendez-vous à organiser, un devis à préparer : les tâches d’organisation prennent du temps, même sans figurer dans l’agenda. Valentina vous accompagne à distance avec les services de secrétariat d’Enkey Agency pour leur accorder l’attention nécessaire.",
    personTitle: "Votre interlocutrice est Valentina",
    personText:
      "Un accompagnement pour les professionnels et les entreprises, adapté aux tâches à réaliser. Avec Valentina, vous définissez ce que vous déléguez, les informations à partager et les étapes soumises à votre validation. Pour un besoin précis ou un suivi mensuel, votre façon de travailler reste le point de départ.",
    hourlyTitle: "Choisissez le service et les heures",
    hourlyIntro:
      "Trois services aux objectifs différents. Sélectionnez celui qui vous convient et indiquez les heures pour voir le total avant d’envoyer votre demande.",
    fitLabel: "À choisir si",
    hourlyUnit: "/heure",
    monthUnit: "/mois",
    vat: "HT",
    choose: "Choisir ce service",
    monthlyTitle: "Assistance avancée aux entreprises",
    monthlyIntro:
      "Un accompagnement mensuel flexible pour coordonner plusieurs activités au-delà d’une tâche ponctuelle. Valentina analyse vos besoins et définit avec vous un plan associant organisation, communications et tâches administratives.",
    monthlyLimit: "Jusqu’à 8 heures au total par mois",
    monthlyDifference:
      "La formule mensuelle privilégie la continuité et la coordination. Les heures sont réparties entre les activités convenues, dans la limite globale de 8 heures. Pour une tâche ciblée, choisissez un service à l’heure.",
    monthlyPoints: [
      "Analyse des besoins et des priorités",
      "Planification conjointe des activités du mois",
      "Accompagnement dans plusieurs domaines selon le plan convenu",
    ],
    processTitle: "De la demande au travail quotidien",
    process: [
      {
        title: "Décrivez votre besoin",
        description:
          "Choisissez le service et les heures, ou la formule mensuelle. Précisez vos tâches et priorités.",
      },
      {
        title: "Définissons la mission",
        description:
          "Nous convenons des activités, délais et documents nécessaires. Pour le mensuel, nous répartissons l’accompagnement dans les 8 heures au total.",
      },
      {
        title: "Organisons le travail",
        description:
          "Nous définissons les outils, informations à partager et étapes de validation pour que Valentina dispose de consignes claires.",
      },
    ],
    requestTitle: "De quel accompagnement avez-vous besoin ?",
    requestIntro:
      "Configurez votre demande et décrivez vos besoins. L’envoi permet de convenir de la mission : aucun paiement n’est effectué et aucune disponibilité ni rendez-vous n’est confirmé automatiquement.",
    serviceLabel: "Service",
    hoursLabel: "Nombre d’heures",
    hoursHint: "Indiquez un nombre entier d’heures, à partir de 1.",
    hoursError: "Indiquez un nombre entier d’heures valide, au moins 1.",
    totalLabel: "Total demandé",
    requestCta: "Demander l’aide de Valentina",
    discover: "Découvrir le service",
    back: "Tous les services",
    summaryLabel: "Récapitulatif de la demande",
    faqTitle: "Avant de déléguer",
    faqs: [
      {
        question: "Quelle différence entre secrétariat, agenda et documents ?",
        answer:
          "Le secrétariat virtuel couvre les tâches administratives et organisationnelles. Le service agenda concerne les rendez-vous, le calendrier et les rappels. Le service documentaire porte sur les devis, offres et formulaires à partir des informations fournies.",
      },
      {
        question: "Que comprend la formule à 300 € par mois ?",
        answer:
          "Un accompagnement avancé et flexible avec analyse des besoins et planification des activités convenues. Jusqu’à 8 heures au total par mois pour l’ensemble du forfait, et non par activité. Le prix est hors taxes.",
      },
      {
        question: "Puis-je choisir le nombre d’heures ?",
        answer:
          "Oui. Pour les trois services horaires, indiquez le nombre d’heures et consultez le total HT. Les activités, délais et disponibilités sont convenus avant le démarrage.",
      },
      {
        question: "Le service est-il à distance ?",
        answer:
          "Oui. Valentina intervient dans le cadre des services d’Enkey Agency. Les outils et informations nécessaires sont définis selon la mission.",
      },
    ],
    services: {
      assistant: {
        title: "Secrétariat virtuel à distance",
        description:
          "Assistance administrative et organisationnelle au quotidien : e-mails, rendez-vous, documents, dossiers et tâches de bureau. Un soutien transversal pour coordonner plusieurs petites tâches.",
        fit: "Vous souhaitez déléguer différentes tâches opérationnelles dans une mission définie.",
      },
      calendar: {
        title: "Gestion d’agenda et de rendez-vous",
        description:
          "Organisation des rendez-vous, planification des activités et envoi de rappels. Le travail se concentre sur le calendrier professionnel pour organiser engagements, disponibilités et échéances.",
        fit: "Votre priorité est la gestion du temps et des rendez-vous sans assistance administrative plus large.",
      },
      documents: {
        title: "Préparation de devis et de documents",
        description:
          "Aide à la préparation de devis, offres, documents commerciaux et formulaires. Valentina organise vos informations et prépare les documents selon vos consignes, pour votre validation.",
        fit: "Vous devez transformer des informations commerciales en documents structurés à vérifier.",
      },
    },
  },
  de: {
    title: "Virtuelle Assistenz mit Valentina",
    meta: "Valentina bei Enkey Agency: Büroservice, Terminplanung und Dokumente. Stundenweise oder erweiterte Betreuung für 300 €/Monat, bis zu 8 Stunden. Zzgl. MwSt.",
    eyebrow: "Enkey Agency · Büroservice aus der Ferne",
    headline: "Mehr Raum für Ihre Arbeit. Mehr Ordnung im Alltag.",
    intro:
      "E-Mails bearbeiten, Termine abstimmen, ein Angebot vorbereiten: Organisation kostet Zeit, auch wenn sie nicht im Kalender steht. Valentina unterstützt Sie aus der Ferne mit dem Büroservice von Enkey Agency, damit alltägliche Aufgaben die nötige Aufmerksamkeit erhalten.",
    personTitle: "Valentina ist Ihre Ansprechpartnerin",
    personText:
      "Unterstützung für Selbstständige und Unternehmen, abgestimmt auf die anstehenden Aufgaben. Mit Valentina legen Sie fest, was Sie delegieren, welche Informationen Sie teilen und welche Schritte Ihre Freigabe benötigen. Bei einzelnen Aufgaben ebenso wie bei monatlicher Betreuung steht Ihre Arbeitsweise im Mittelpunkt.",
    hourlyTitle: "Service und Stunden selbst wählen",
    hourlyIntro:
      "Drei Leistungen mit unterschiedlichen Schwerpunkten. Wählen Sie den passenden Service und die Stunden, um vor der Anfrage den Gesamtbetrag zu sehen.",
    fitLabel: "Passend, wenn",
    hourlyUnit: "/Stunde",
    monthUnit: "/Monat",
    vat: "Zzgl. MwSt.",
    choose: "Diesen Service wählen",
    monthlyTitle: "Erweiterte Unternehmensassistenz",
    monthlyIntro:
      "Flexible monatliche Betreuung zur Koordination mehrerer Tätigkeiten über eine einzelne Aufgabe hinaus. Valentina bespricht Ihren Bedarf und vereinbart mit Ihnen einen Plan für Organisation, Kommunikation und Büroarbeit.",
    monthlyLimit: "Bis zu 8 Stunden insgesamt pro Monat",
    monthlyDifference:
      "Das Monatsmodell dient der kontinuierlichen Betreuung und Koordination. Die Stunden werden innerhalb der Gesamtgrenze von 8 Stunden auf die vereinbarten Tätigkeiten verteilt. Für eine klar umrissene Aufgabe eignet sich ein Stundentarif.",
    monthlyPoints: [
      "Bedarfsanalyse und Festlegung der Prioritäten",
      "Gemeinsame Planung der monatlichen Aufgaben",
      "Unterstützung in mehreren Bereichen nach vereinbartem Plan",
    ],
    processTitle: "Von der Anfrage zur täglichen Arbeit",
    process: [
      {
        title: "Beschreiben Sie Ihren Bedarf",
        description:
          "Wählen Sie Service und Stunden oder das Monatsmodell. Ergänzen Sie Aufgaben und Prioritäten.",
      },
      {
        title: "Den Auftrag gemeinsam festlegen",
        description:
          "Wir vereinbaren Tätigkeiten, Zeitrahmen und benötigte Unterlagen. Beim Monatsmodell verteilen wir die Unterstützung auf insgesamt bis zu 8 Stunden.",
      },
      {
        title: "Die Arbeit organisieren",
        description:
          "Wir legen Werkzeuge, benötigte Informationen und Freigabeschritte fest, damit Valentina nach klaren Vorgaben arbeiten kann.",
      },
    ],
    requestTitle: "Welche Unterstützung benötigen Sie?",
    requestIntro:
      "Stellen Sie Ihre Anfrage zusammen und beschreiben Sie Ihren Bedarf. Das Absenden dient der Auftragsklärung: Es erfolgt keine Zahlung und keine automatische Bestätigung von Verfügbarkeit oder Terminen.",
    serviceLabel: "Service",
    hoursLabel: "Anzahl der Stunden",
    hoursHint: "Geben Sie eine ganze Stundenzahl ab 1 ein.",
    hoursError:
      "Geben Sie eine gültige ganze Stundenzahl von mindestens 1 ein.",
    totalLabel: "Angefragter Gesamtbetrag",
    requestCta: "Unterstützung von Valentina anfragen",
    discover: "Service entdecken",
    back: "Alle Leistungen",
    summaryLabel: "Zusammenfassung der Anfrage",
    faqTitle: "Bevor Sie Aufgaben delegieren",
    faqs: [
      {
        question: "Was unterscheidet Büroservice, Terminplanung und Dokumente?",
        answer:
          "Die virtuelle Assistenz umfasst administrative und organisatorische Büroaufgaben. Die Terminplanung konzentriert sich auf Termine, Kalender und Erinnerungen. Der Dokumentenservice umfasst Angebote und Formulare auf Grundlage Ihrer Informationen.",
      },
      {
        question: "Was umfasst das Monatsmodell für 300 €?",
        answer:
          "Erweiterte, flexible Betreuung mit Bedarfsanalyse und Planung vereinbarter Tätigkeiten. Enthalten sind insgesamt bis zu 8 Stunden pro Monat für das gesamte Paket, nicht pro Tätigkeit. Der Preis versteht sich zuzüglich Mehrwertsteuer.",
      },
      {
        question: "Kann ich die Stundenanzahl wählen?",
        answer:
          "Ja. Bei den drei Stundentarifen geben Sie die Stunden ein und sehen den Gesamtbetrag ohne Mehrwertsteuer. Aufgaben, Zeitrahmen und Verfügbarkeit werden vor Beginn vereinbart.",
      },
      {
        question: "Erfolgt die Unterstützung aus der Ferne?",
        answer:
          "Ja. Valentina unterstützt Sie im Rahmen der Leistungen von Enkey Agency. Werkzeuge und benötigte Informationen werden je nach Auftrag vereinbart.",
      },
    ],
    services: {
      assistant: {
        title: "Virtuelle Assistenz aus der Ferne",
        description:
          "Administrative und organisatorische Unterstützung im Alltag: E-Mails, Termine, Dokumente, Vorgänge und Büroarbeit. Bereichsübergreifende Hilfe, wenn mehrere kleinere Aufgaben zusammengeführt werden müssen.",
        fit: "Sie verschiedene operative Aufgaben innerhalb eines definierten Auftrags delegieren möchten.",
      },
      calendar: {
        title: "Kalender- und Terminverwaltung",
        description:
          "Organisation von Terminen, Planung von Tätigkeiten und Versand von Erinnerungen. Im Mittelpunkt steht Ihr beruflicher Kalender mit Verpflichtungen, Verfügbarkeiten und Fristen.",
        fit: "Sie vor allem Zeit und Termine organisieren möchten, ohne umfassendere Büroassistenz.",
      },
      documents: {
        title: "Angebote und Geschäftsdokumente",
        description:
          "Unterstützung bei Angeboten, Geschäftsdokumenten und Formularen. Valentina ordnet Ihre Informationen und bereitet die Unterlagen nach Ihren Vorgaben zur Prüfung vor.",
        fit: "Sie geschäftliche Informationen in geordnete, prüfbare Dokumente umwandeln müssen.",
      },
    },
  },
};

export function secretariatPath(locale: SupportedLocale): string {
  return `${localizedPath("services", locale)}/${SECRETARIAT_SLUGS[locale]}`;
}
export function isSecretariatSlug(slug: string | undefined): boolean {
  return Object.values(SECRETARIAT_SLUGS).includes(slug ?? "");
}
export function secretariatAlternates() {
  return (Object.keys(SECRETARIAT_SLUGS) as SupportedLocale[]).map(
    (locale) => ({ locale, path: secretariatPath(locale) }),
  );
}
export function isHourlyService(plan: SecretariatPlan): plan is HourlyService {
  return Object.hasOwn(SECRETARIAT_RATES, plan);
}
export function parseSecretariatHours(value: string): number | null {
  // The UI collects whole hours. Reject empty, fractional, signed and exponent input.
  if (!/^\d+$/.test(value)) return null;
  const hours = Number(value);
  return Number.isSafeInteger(hours) &&
    hours >= 1 &&
    Number.isSafeInteger(hours * 3500)
    ? hours
    : null;
}
export function secretariatMoney(
  value: number,
  locale: SupportedLocale,
): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(value);
}
export function secretariatSummary(
  plan: SecretariatPlan,
  hoursText: string,
  locale: SupportedLocale,
) {
  const copy = SECRETARIAT_COPY[locale];
  if (plan === "monthly")
    return `${copy.monthlyTitle} · ${secretariatMoney(MONTHLY_PRICE, locale)}${copy.monthUnit} · ${copy.monthlyLimit} · ${copy.vat}`;
  const hours = parseSecretariatHours(hoursText);
  if (hours === null) return null;
  return `${copy.services[plan].title} · ${hours} × ${secretariatMoney(SECRETARIAT_RATES[plan], locale)}${copy.hourlyUnit} = ${secretariatMoney(hours * SECRETARIAT_RATES[plan], locale)} · ${copy.vat}`;
}
export function secretariatJsonLd(
  locale: SupportedLocale,
): Record<string, unknown> {
  const copy = SECRETARIAT_COPY[locale];
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: copy.title,
    description: copy.meta,
    url: `${SITE_URL}${secretariatPath(locale)}`,
    provider: { "@id": `${SITE_URL}/#organization` },
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: copy.title,
      itemListElement: [
        ...Object.entries(SECRETARIAT_RATES).map(([key, rate]) => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: copy.services[key as HourlyService].title,
          },
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: rate,
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            referenceQuantity: {
              "@type": "QuantitativeValue",
              value: 1,
              unitCode: "HUR",
            },
          },
        })),
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: copy.monthlyTitle,
            description: copy.monthlyLimit,
          },
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: MONTHLY_PRICE,
            priceCurrency: "EUR",
            valueAddedTaxIncluded: false,
            referenceQuantity: {
              "@type": "QuantitativeValue",
              value: 1,
              unitCode: "MON",
            },
          },
        },
      ],
    },
  };
}
